import { calendarDate } from '../../utils/calendarDate';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  CircularProgress,
  Chip,
  Link,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  Tooltip
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import api from '../api/api';

const FinancialReportsPage = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const loadVersion = useRef(0);
  const [loadError, setLoadError] = useState('');
  const errorMessage = (error, fallback) => typeof error?.response?.data?.detail === 'string' ? error.response.data.detail : fallback;
  useEffect(() => () => { loadVersion.current++; }, []);
  const [summary, setSummary] = useState({
    total_income: 0,
    total_expense: 0,
    profit: 0
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  // Вычисляем дефолтные даты для текущего месяца
  const getDefaultMonthDates = () => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
      start: calendarDate(start),
      end: calendarDate(end)
    };
  };

  const defaultDates = getDefaultMonthDates();

  // Фильтры - по умолчанию текущий месяц
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);

  // Снекбар для уведомлений
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Диалог импорта
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  // Диалог редактирования
  const [editDialog, setEditDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editAmount, setEditAmount] = useState('');

  // Диалог подтверждения удаления
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deletingTransaction, setDeletingTransaction] = useState(null);

  // Выбранные записи для массового удаления
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Быстрые фильтры
  const setQuickFilter = (filterType) => {
    setPage(1);
    const today = new Date();
    let start, end;

    switch (filterType) {
      case 'month':
        // Текущий месяц
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'year':
        // Текущий год
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today.getFullYear(), 11, 31);
        break;
      case 'all':
        // Все время
        setStartDate('');
        setEndDate('');
        return;
      default:
        return;
    }

    setStartDate(calendarDate(start));
    setEndDate(calendarDate(end));
  };

  useEffect(() => {
    setSelectedIds([]);
    fetchData();
  }, [page, pageSize, startDate, endDate]);

  const fetchData = async () => {
    const version = ++loadVersion.current;
    setLoadError('');
    setLoading(true);
    try {
      await Promise.all([fetchTransactions(version), fetchSummary(version)]);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
    } finally {
      if (version === loadVersion.current) setLoading(false);
    }
  };

  const fetchTransactions = async (version) => {
    try {
      const params = {
        page,
        limit: pageSize,
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate })
      };

      const response = await api.get('/reports/financial', { params });
      if (version !== loadVersion.current) return;
      setTransactions(response.data.transactions || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Ошибка загрузки транзакций:', error);
      if (version === loadVersion.current) setLoadError('Не удалось загрузить финансовый отчёт');
    }
  };

  const fetchSummary = async (version) => {
    try {
      const params = {
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate })
      };

      const response = await api.get('/reports/financial/summary', { params });
      if (version === loadVersion.current) setSummary(response.data);
    } catch (error) {
      console.error('Ошибка загрузки сводки:', error);
      if (version === loadVersion.current) setLoadError('Не удалось загрузить финансовый отчёт');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    
    // Проверяем есть ли время (если часы и минуты = 00:00, значит это только дата)
    const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0;
    
    if (hasTime) {
      // Показываем дату и время
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } else {
      // Показываем только дату
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
  };

  const getTypeColor = (type) => {
    return type === 'income' ? 'success' : 'error';
  };

  // Функция для рендеринга описания со ссылками на сделки
  const renderDescription = (description, dealId) => {
    if (!description) return '-';
    
    // Ищем номера сделок в формате "№123" или "#123"
    const dealPattern = /[№#](\d+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = dealPattern.exec(description)) !== null) {
      // Добавляем текст до найденного номера
      if (match.index > lastIndex) {
        parts.push(description.substring(lastIndex, match.index));
      }
      
      // Добавляем ссылку на сделку (если есть deal_id - используем его)
      const linkDealId = dealId;
      parts.push(
        linkDealId ? (
          <Link
            key={match.index}
            component="button"
            variant="body2"
            onClick={() => navigate(`/rental-deals/edit/${linkDealId}`)}
            sx={{ 
              cursor: 'pointer',
              textDecoration: 'underline',
              color: 'primary.main',
              fontWeight: 'bold'
            }}
          >
            {match[0]}
          </Link>
        ) : (
          <span key={match.index} style={{ fontWeight: 'bold' }}>{match[0]}</span>
        )
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Добавляем оставшийся текст
    if (lastIndex < description.length) {
      parts.push(description.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : description;
  };

  // Экспорт транзакций в Excel
  const handleExport = async () => {
    try {
      const params = {
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate })
      };

      const response = await api.get('/reports/financial/export', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileName = `финансовый_отчет_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: 'Отчет успешно экспортирован!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Ошибка экспорта:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при экспорте отчета',
        severity: 'error'
      });
    }
  };

  // Импорт транзакций из Excel
  const handleImport = async () => {
    if (!importFile) {
      setSnackbar({
        open: true,
        message: 'Выберите файл для импорта',
        severity: 'warning'
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      setImporting(true);
      const response = await api.post('/reports/financial/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSnackbar({
        open: true,
        message: `Импортировано транзакций: ${response.data.imported || 0}`,
        severity: 'success'
      });

      setImportDialog(false);
      setImportFile(null);
      
      // Перезагружаем данные
      fetchData();
    } catch (error) {
      console.error('Ошибка импорта:', error);
      setSnackbar({
        open: true,
        message: errorMessage(error, 'Ошибка при импорте файла'),
        severity: 'error'
      });
    } finally {
      setImporting(false);
    }
  };

  // Скачать шаблон для импорта
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/reports/financial/template', {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'шаблон_финансовый_импорт.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: 'Шаблон успешно скачан!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Ошибка скачивания шаблона:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при скачивании шаблона',
        severity: 'error'
      });
    }
  };

  // Открыть диалог редактирования
  const handleEditClick = (transaction) => {
    setEditingTransaction(transaction);
    setEditAmount(Math.abs(Number(transaction.amount)).toString());
    setEditDialog(true);
  };

  // Сохранить изменения транзакции
  const handleEditSave = async () => {
    if (!editingTransaction || !editAmount) {
      setSnackbar({
        open: true,
        message: 'Укажите сумму',
        severity: 'warning'
      });
      return;
    }

    if (!Number.isFinite(Number(editAmount)) || Number(editAmount) <= 0) {
      setSnackbar({open:true, message:'Сумма должна быть больше нуля', severity:'warning'});
      return;
    }
    try {
      await api.put(`/reports/financial/${editingTransaction.id}`, {
        amount: parseFloat(editAmount)
      });

      setSnackbar({
        open: true,
        message: 'Транзакция успешно обновлена!',
        severity: 'success'
      });

      setEditDialog(false);
      setEditingTransaction(null);
      setEditAmount('');
      
      // Перезагружаем данные
      fetchData();
    } catch (error) {
      console.error('Ошибка обновления транзакции:', error);
      setSnackbar({
        open: true,
        message: errorMessage(error, 'Ошибка при обновлении транзакции'),
        severity: 'error'
      });
    }
  };

  // Открыть диалог удаления
  const handleDeleteClick = (transaction) => {
    setDeletingTransaction(transaction);
    setDeleteDialog(true);
  };

  // Подтвердить удаление
  const handleDeleteConfirm = async () => {
    if (!deletingTransaction) return;

    try {
      await api.delete(`/reports/financial/${deletingTransaction.id}`);

      setSnackbar({
        open: true,
        message: 'Транзакция успешно удалена!',
        severity: 'success'
      });

      setDeleteDialog(false);
      setDeletingTransaction(null);
      
      // Перезагружаем данные
      fetchData();
    } catch (error) {
      console.error('Ошибка удаления транзакции:', error);
      setSnackbar({
        open: true,
        message: errorMessage(error, 'Ошибка при удалении транзакции'),
        severity: 'error'
      });
    }
  };

  // Обработка выбора всех записей на странице
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = transactions.filter(t => !t.read_only).map(t => t.id);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  // Обработка выбора одной записи
  const handleSelectOne = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(item => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Проверка выбрана ли запись
  const isSelected = (id) => selectedIds.includes(id);

  // Все ли записи на странице выбраны
  const selectableCount = transactions.filter(t => !t.read_only).length;
  const isAllSelected = selectableCount > 0 && selectedIds.length === selectableCount;

  // Некоторые записи выбраны
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < selectableCount;

  // Массовое удаление выбранных
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    try {
      setBulkDeleting(true);
      
      // Используем batch endpoint
      const response = await api.post('/reports/financial/bulk-delete', { ids: selectedIds });
      const { deleted_count, errors } = response.data;

      setSnackbar({
        open: true,
        message: `Удалено транзакций: ${deleted_count}${errors && errors.length > 0 ? `, ошибок: ${errors.length}` : ''}`,
        severity: errors && errors.length > 0 ? 'warning' : 'success'
      });

      setBulkDeleteDialog(false);
      setSelectedIds([]);
      
      // Перезагружаем данные
      fetchData();
    } catch (error) {
      console.error('Ошибка массового удаления:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при массовом удалении',
        severity: 'error'
      });
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <Container className="erp-report-page" maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {loadError && <Alert severity="error" sx={{mb:2}}>{loadError}</Alert>}
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
        📊 Финансовые отчеты
      </Typography>

      {/* Карточки сводки */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #0878ff 0%, #244a77 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Приход
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {formatCurrency(summary.total_income)}
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 50, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #a5652c 0%, #ba834e 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Расход
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {formatCurrency(summary.total_expense)}
                  </Typography>
                </Box>
                <TrendingDownIcon sx={{ fontSize: 50, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ 
            background: summary.profit >= 0 
              ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' 
              : 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: 'white' 
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    {summary.profit >= 0 ? 'Прибыль' : 'Убыток'}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {formatCurrency(Math.abs(summary.profit))}
                  </Typography>
                </Box>
                <AccountBalanceIcon sx={{ fontSize: 50, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Фильтры */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          Фильтры
        </Typography>
        
        {/* Быстрые фильтры */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Быстрый выбор:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setQuickFilter('month')}
              sx={{ textTransform: 'none' }}
            >
              Месяц
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setQuickFilter('year')}
              sx={{ textTransform: 'none' }}
            >
              Год
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setQuickFilter('all')}
              sx={{ textTransform: 'none' }}
            >
              Все время
            </Button>
          </Box>
        </Box>

        {/* Ручной выбор дат */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Дата от"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Дата до"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setStartDate('');
                setEndDate('');
                setPage(1);
              }}
              sx={{ height: '56px' }}
            >
              Сбросить
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Таблица транзакций */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Движение денежных средств
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Всего: {total} записей
            </Typography>
            
            {/* Выбор количества записей */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Показывать:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(e.target.value);
                    setPage(1);
                  }}
                  sx={{ height: 32 }}
                >
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {/* Кнопка удаления выбранных */}
            {selectedIds.length > 0 && (
              <Tooltip title={`Удалить выбранные (${selectedIds.length})`}>
                <IconButton
                  color="error"
                  onClick={() => setBulkDeleteDialog(true)}
                  sx={{ ml: 1 }}
                >
                  <DeleteSweepIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          
          {/* Кнопки импорта/экспорта */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Экспорт
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileUploadIcon />}
              onClick={() => setImportDialog(true)}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Импорт
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={isSomeSelected}
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell><strong>Дата</strong></TableCell>
                    <TableCell><strong>Тип</strong></TableCell>
                    <TableCell><strong>Категория</strong></TableCell>
                    <TableCell><strong>Описание</strong></TableCell>
                    <TableCell><strong>Клиент</strong></TableCell>
                    <TableCell align="right"><strong>Сумма</strong></TableCell>
                    <TableCell><strong>Метод оплаты</strong></TableCell>
                    <TableCell align="center"><strong>Действия</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.length === 0 && <TableRow><TableCell colSpan={8} align="center">Нет данных за выбранный период</TableCell></TableRow>}
                  {transactions.map((transaction) => (
                    <TableRow 
                      key={transaction.id} 
                      hover
                      selected={isSelected(transaction.id)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected(transaction.id)}
                          disabled={transaction.read_only}
                          onChange={() => handleSelectOne(transaction.id)}
                        />
                      </TableCell>
                      <TableCell>{formatDate(transaction.date)}</TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.type_label}
                          color={getTypeColor(transaction.type)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{transaction.category_label}</TableCell>
                      <TableCell>{renderDescription(transaction.description, transaction.deal_id)}</TableCell>
                      <TableCell>{transaction.client_name || '-'}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            color: transaction.type === 'income' ? 'success.main' : 'error.main'
                          }}
                        >
                          {transaction.type === 'income' ? '+' : '−'}{formatCurrency(Math.abs(transaction.amount))}
                        </Typography>
                      </TableCell>
                      <TableCell>{transaction.payment_method === 'refund_test' ? 'Тестовый возврат' : transaction.payment_method || '-'}</TableCell>
                      <TableCell align="center">
                        {transaction.read_only ? <Typography variant="caption" color="text.secondary">По возврату вещи</Typography> : <>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditClick(transaction)}
                          title="Редактировать"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(transaction)}
                          title="Удалить"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        </>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Пагинация */}
            {total > pageSize && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 2 }}>
                <Button
                  variant="outlined"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  Назад
                </Button>
                <Typography sx={{ alignSelf: 'center' }}>
                  Страница {page} из {Math.ceil(total / pageSize)}
                </Typography>
                <Button
                  variant="outlined"
                  disabled={page >= Math.ceil(total / pageSize)}
                  onClick={() => setPage(page + 1)}
                >
                  Вперед
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Диалог редактирования */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Редактировать транзакцию</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Тип:</strong> {editingTransaction?.type_label}<br/>
              <strong>Категория:</strong> {editingTransaction?.category_label}<br/>
              <strong>Описание:</strong> {editingTransaction?.description}<br/>
              <strong>Дата:</strong> {formatDate(editingTransaction?.date)}
            </Typography>
            
            <TextField
              fullWidth
              type="number"
              label="Сумма"
              value={editAmount}
              onChange={(e) => setEditAmount(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>
            Отмена
          </Button>
          <Button onClick={handleEditSave} variant="contained" color="primary">
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Подтвердите удаление</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Вы уверены, что хотите удалить эту транзакцию?
          </Typography>
          {deletingTransaction && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Тип:</strong> {deletingTransaction.type_label}<br/>
                <strong>Категория:</strong> {deletingTransaction.category_label}<br/>
                <strong>Сумма:</strong> {formatCurrency(deletingTransaction.amount)}<br/>
                <strong>Дата:</strong> {formatDate(deletingTransaction.date)}<br/>
                <strong>Описание:</strong> {deletingTransaction.description}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>
            Отмена
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог подтверждения массового удаления */}
      <Dialog open={bulkDeleteDialog} onClose={() => setBulkDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Подтвердите массовое удаление</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Вы уверены, что хотите удалить {selectedIds.length} транзакций?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Это действие нельзя отменить.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialog(false)} disabled={bulkDeleting}>
            Отмена
          </Button>
          <Button onClick={handleBulkDelete} variant="contained" color="error" disabled={bulkDeleting}>
            {bulkDeleting ? 'Удаление...' : 'Удалить выбранные'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог импорта */}
      <Dialog open={importDialog} onClose={() => setImportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Импорт финансовых операций</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleDownloadTemplate}
              sx={{ mb: 2 }}
            >
              Скачать шаблон Excel
            </Button>
          </Box>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            Заполните шаблон и загрузите его для импорта финансовых операций.
          </Typography>
          
          <Typography variant="caption" component="div" sx={{ mb: 2, color: 'text.secondary' }}>
            <strong>Обязательные поля:</strong><br/>
            • Дата и время оплаты (дд.мм.гггг чч:мм)<br/>
            • Сумма (число)<br/>
            • Тип оплаты (Наличные/Безналичный расчет/Онлайн/Терминал)<br/>
            <br/>
            <strong>Дополнительные поля:</strong><br/>
            • Сделка (номер сделки, если нужна привязка)<br/>
            • Сотрудник (информационное поле)<br/>
            • Пункт проката (информационное поле)
          </Typography>
          
          <Button
            variant="contained"
            component="label"
            startIcon={<FileUploadIcon />}
            fullWidth
          >
            Выбрать файл
            <input
              type="file"
              hidden
              accept=".xlsx,.xls"
              onChange={(e) => setImportFile(e.target.files[0])}
            />
          </Button>
          
          {importFile && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Выбран файл: {importFile.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialog(false)} disabled={importing}>
            Отмена
          </Button>
          <Button 
            onClick={handleImport} 
            variant="contained" 
            disabled={!importFile || importing}
          >
            {importing ? 'Импорт...' : 'Импортировать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Снекбар для уведомлений */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FinancialReportsPage;
